import { motion } from 'framer-motion'
import { 
  ArrowLeft, 
  LogOut,
  Calculator,
  FileText,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Clock,
  Users,
  BarChart3,
  PieChart,
  Calendar,
  Bot,
  Zap,
  Target,
  Activity
} from 'lucide-react'
import InfoTooltip from '../common/InfoTooltip'

export default function ContableDashboard({ onBack, onLogout, businessData }) {
  const kpis = [
    {
      title: 'Automatización Fiscal',
      value: '85%',
      change: '+12%',
      trend: 'up',
      icon: Zap,
      color: 'blue',
      tooltip: {
        title: 'Automatización Fiscal',
        description: 'Porcentaje de procesos fiscales que se ejecutan automáticamente sin intervención manual.',
        aiMethod: 'Machine Learning para clasificación de documentos, OCR para extracción de datos, y reglas de negocio inteligentes para validación automática.',
        dataSource: 'Histórico de transacciones, documentos fiscales procesados, tiempos de ejecución manual vs automática.',
        updateFrequency: 'Tiempo real con consolidación diaria'
      }
    },
    {
      title: 'Tiempo Ahorrado',
      value: '60h',
      change: '+15h',
      trend: 'up',
      icon: Clock,
      color: 'green',
      suffix: '/mes',
      tooltip: {
        title: 'Tiempo Ahorrado por Automatización',
        description: 'Horas de trabajo manual eliminadas gracias a la automatización de procesos contables y fiscales.',
        aiMethod: 'Análisis de tiempos históricos vs tiempos con IA. Algoritmos de optimización de procesos.',
        dataSource: 'Registros de tiempo por tarea, comparativa antes/después de implementación IA.',
        updateFrequency: 'Cálculo mensual basado en datos diarios'
      }
    },
    {
      title: 'Precisión de Cálculos',
      value: '99.2%',
      change: '+0.8%',
      trend: 'up',
      icon: Target,
      color: 'purple',
      tooltip: {
        title: 'Precisión de Cálculos Automáticos',
        description: 'Porcentaje de cálculos fiscales y contables realizados correctamente por el sistema de IA.',
        aiMethod: 'Validación cruzada con múltiples algoritmos, verificación contra bases de datos oficiales del SAT.',
        dataSource: 'Auditorías internas, validaciones con SAT, comparativa con cálculos manuales certificados.',
        updateFrequency: 'Validación continua con reporte semanal'
      }
    },
    {
      title: 'Clientes Atendidos',
      value: '142',
      change: '+28',
      trend: 'up',
      icon: Users,
      color: 'orange',
      tooltip: {
        title: 'Clientes Atendidos con IA',
        description: 'Número de clientes que han utilizado servicios automatizados con inteligencia artificial.',
        aiMethod: 'Sistema de gestión inteligente que optimiza la carga de trabajo y asignación automática de tareas.',
        dataSource: 'Base de datos de clientes, registros de servicios utilizados, nivel de automatización por cliente.',
        updateFrequency: 'Actualización en tiempo real'
      }
    }
  ]

  const processesData = [
    {
      name: 'Cálculo de ISR',
      automation: 95,
      timeReduction: 85,
      accuracy: 99.5,
      color: 'blue',
      tooltip: {
        title: 'Automatización de Cálculo ISR',
        description: 'Sistema que calcula automáticamente el Impuesto Sobre la Renta basado en ingresos, deducciones y tablas fiscales vigentes.',
        aiMethod: 'Algoritmos de cálculo fiscal con validación en tiempo real contra tablas del SAT. Machine Learning para optimización de deducciones.',
        dataSource: 'Registros contables, catálogo de deducciones, tablas fiscales SAT, historial de declaraciones.',
        updateFrequency: 'Procesamiento instantáneo con validación diaria'
      }
    },
    {
      name: 'Conciliaciones Bancarias',
      automation: 88,
      timeReduction: 75,
      accuracy: 98.8,
      color: 'green',
      tooltip: {
        title: 'Conciliación Bancaria Automática',
        description: 'Matching inteligente entre movimientos bancarios y registros contables para identificar discrepancias automáticamente.',
        aiMethod: 'Algoritmos de matching fuzzy, reconocimiento de patrones en descripciones, ML para clasificación de transacciones.',
        dataSource: 'Estados de cuenta bancarios, registros contables, catálogo de cuentas, historial de conciliaciones.',
        updateFrequency: 'Sincronización diaria con bancos'
      }
    },
    {
      name: 'Facturación CFDI',
      automation: 92,
      timeReduction: 80,
      accuracy: 99.9,
      color: 'purple',
      tooltip: {
        title: 'Generación Automática de CFDI',
        description: 'Creación y timbrado automático de Comprobantes Fiscales Digitales con validación completa ante el SAT.',
        aiMethod: 'Validación inteligente de RFC, códigos postales, productos/servicios SAT. Auto-completado basado en historial.',
        dataSource: 'Catálogo SAT de productos y servicios, base de datos de clientes, configuración fiscal por empresa.',
        updateFrequency: 'Generación en tiempo real'
      }
    },
    {
      name: 'Declaraciones',
      automation: 78,
      timeReduction: 65,
      accuracy: 98.2,
      color: 'orange',
      tooltip: {
        title: 'Preparación de Declaraciones Fiscales',
        description: 'Generación automática de declaraciones mensuales y anuales con cálculos precisos y validaciones fiscales.',
        aiMethod: 'Extracción inteligente de datos contables, cálculos automáticos de impuestos, validación de cumplimiento fiscal.',
        dataSource: 'Registros contables del período, facturas emitidas/recibidas, pagos realizados, deducciones aplicables.',
        updateFrequency: 'Preparación mensual con actualización continua'
      }
    }
  ]

  const recentAlerts = [
    {
      type: 'warning',
      message: 'Cliente Empresa ABC - Fecha límite declaración: 3 días',
      time: 'Hace 2 horas'
    },
    {
      type: 'success',
      message: 'Conciliación bancaria automatizada completada para 15 clientes',
      time: 'Hace 4 horas'
    },
    {
      type: 'info',
      message: 'Nueva actualización de SAT detectada - Actualizando reglas automáticamente',
      time: 'Hace 6 horas'
    }
  ]

  const monthlyStats = [
    { month: 'Ene', traditional: 180, ai_assisted: 95 },
    { month: 'Feb', traditional: 165, ai_assisted: 85 },
    { month: 'Mar', traditional: 140, ai_assisted: 75 },
    { month: 'Abr', traditional: 120, ai_assisted: 65 },
    { month: 'May', traditional: 95, ai_assisted: 45 },
    { month: 'Jun', traditional: 85, ai_assisted: 35 }
  ]

  const clientSatisfaction = [
    { category: 'Velocidad', score: 96 },
    { category: 'Precisión', score: 98 },
    { category: 'Comunicación', score: 94 },
    { category: 'Disponibilidad', score: 92 },
    { category: 'Costo-Beneficio', score: 97 }
  ]

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
      red: 'bg-red-100 text-red-800'
    }
    return colors[color] || colors.blue
  }

  const getIconColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500'
    }
    return colors[color] || colors.blue
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </button>
              <div className="flex items-center">
                <div className={`w-8 h-8 bg-gradient-to-br ${businessData.color} rounded-lg flex items-center justify-center mr-3`}>
                  <Calculator className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{businessData.title}</h1>
                  <p className="text-sm text-gray-500">Dashboard Analytics con IA</p>
                </div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* KPIs Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
                  {kpis.map((kpi, index) => {
          const IconComponent = kpi.icon
          return (
            <div key={index} className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-gray-600">{kpi.title}</p>
                    <InfoTooltip 
                      title={kpi.tooltip.title}
                      description={kpi.tooltip.description}
                      aiMethod={kpi.tooltip.aiMethod}
                      dataSource={kpi.tooltip.dataSource}
                      updateFrequency={kpi.tooltip.updateFrequency}
                      iconColor="text-gray-400"
                    />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {kpi.value}{kpi.suffix || ''}
                  </p>
                  <div className="flex items-center mt-2">
                    <span className={`text-sm font-medium ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                      {kpi.change}
                    </span>
                    <span className="text-sm text-gray-500 ml-1">vs mes anterior</span>
                  </div>
                </div>
                <div className={`p-3 ${getIconColorClasses(kpi.color)} rounded-lg`}>
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          )
        })}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Automatización por Proceso */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Automatización por Proceso</h3>
                <div className="flex items-center space-x-2">
                  <Bot className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-gray-600">IA Activa</span>
                </div>
              </div>
              
              <div className="space-y-6">
                {processesData.map((process, index) => (
                  <div key={index} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-900">{process.name}</h4>
                        <InfoTooltip 
                          title={process.tooltip.title}
                          description={process.tooltip.description}
                          aiMethod={process.tooltip.aiMethod}
                          dataSource={process.tooltip.dataSource}
                          updateFrequency={process.tooltip.updateFrequency}
                          iconColor="text-gray-400"
                        />
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getColorClasses(process.color)}`}>
                        {process.automation}% automatizado
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Automatización</p>
                        <div className="mt-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${getIconColorClasses(process.color)}`}
                            style={{ width: `${process.automation}%` }}
                          />
                        </div>
                        <p className="mt-1 font-medium">{process.automation}%</p>
                      </div>
                      
                      <div>
                        <p className="text-gray-600">Reducción Tiempo</p>
                        <div className="mt-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-green-500"
                            style={{ width: `${process.timeReduction}%` }}
                          />
                        </div>
                        <p className="mt-1 font-medium">{process.timeReduction}%</p>
                      </div>
                      
                      <div>
                        <p className="text-gray-600">Precisión</p>
                        <div className="mt-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-purple-500"
                            style={{ width: `${process.accuracy}%` }}
                          />
                        </div>
                        <p className="mt-1 font-medium">{process.accuracy}%</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Comparación Mensual */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Horas de Trabajo: Tradicional vs IA Asistido
                </h3>
                <InfoTooltip 
                  title="Comparativo de Eficiencia Temporal"
                  description="Análisis comparativo de horas invertidas en procesos contables usando métodos tradicionales versus asistencia de IA."
                  aiMethod="Análisis de tiempo por tarea, machine learning para optimización de procesos, automatización de tareas repetitivas."
                  dataSource="Registros de tiempo por empleado, histórico de tareas, medición antes/después de implementación IA."
                  updateFrequency="Análisis mensual con datos capturados diariamente"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {monthlyStats.map((stat, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="w-12 text-sm font-medium text-gray-700">{stat.month}</div>
                    
                    <div className="flex-1 flex items-center space-x-4">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">Tradicional</span>
                          <span className="text-sm font-medium">{stat.traditional}h</span>
                        </div>
                        <div className="bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-red-400"
                            style={{ width: `${(stat.traditional / 200) * 100}%` }}
                          />
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">Con IA</span>
                          <span className="text-sm font-medium">{stat.ai_assisted}h</span>
                        </div>
                        <div className="bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-blue-500"
                            style={{ width: `${(stat.ai_assisted / 200) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <span className="text-sm font-medium text-green-600">
                        -{Math.round(((stat.traditional - stat.ai_assisted) / stat.traditional) * 100)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Impacto acumulado:</strong> Se han ahorrado 495 horas de trabajo en 6 meses, 
                  equivalente a $49,500 MXN en costos operativos.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Alertas Recientes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Alertas Inteligentes</h3>
                <InfoTooltip 
                  title="Sistema de Alertas Inteligentes"
                  description="Notificaciones automáticas generadas por IA para identificar situaciones que requieren atención inmediata o acciones preventivas."
                  aiMethod="Análisis predictivo de patrones, machine learning para detectar anomalías, reglas de negocio inteligentes."
                  dataSource="Datos en tiempo real de clientes, fechas de vencimiento, patrones históricos, indicadores de riesgo."
                  updateFrequency="Monitoreo continuo con alertas instantáneas"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {recentAlerts.map((alert, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className={`p-2 rounded-full flex-shrink-0 ${
                      alert.type === 'warning' ? 'bg-yellow-100' :
                      alert.type === 'success' ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      {alert.type === 'warning' && <AlertTriangle className="w-4 h-4 text-yellow-600" />}
                      {alert.type === 'success' && <CheckCircle className="w-4 h-4 text-green-600" />}
                      {alert.type === 'info' && <Activity className="w-4 h-4 text-blue-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{alert.message}</p>
                      <p className="text-xs text-gray-500 mt-1">{alert.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Satisfacción del Cliente */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Satisfacción del Cliente</h3>
                <InfoTooltip 
                  title="Medición de Satisfacción del Cliente"
                  description="Análisis integral de la satisfacción del cliente basado en múltiples indicadores de calidad de servicio y eficiencia."
                  aiMethod="Análisis de sentimientos en comunicaciones, scoring automático de calidad, predicción de satisfacción."
                  dataSource="Encuestas de satisfacción, tiempos de respuesta, calidad de entregas, feedback directo de clientes."
                  updateFrequency="Actualización semanal con análisis continuo"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {clientSatisfaction.map((item, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">{item.category}</span>
                      <span className="text-sm font-bold text-gray-900">{item.score}%</span>
                    </div>
                    <div className="bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          item.score >= 95 ? 'bg-green-500' :
                          item.score >= 90 ? 'bg-blue-500' : 'bg-yellow-500'
                        }`}
                        style={{ width: `${item.score}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Promedio General</p>
                    <p className="text-lg font-bold text-green-900">95.4%</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Próximas Tareas IA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Próximas Tareas IA</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Bot className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium">Procesar 25 CFDI</span>
                  </div>
                  <span className="text-xs text-blue-600">En 2 horas</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Calculator className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium">Cálculo ISR automático</span>
                  </div>
                  <span className="text-xs text-purple-600">Mañana 9:00</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <BarChart3 className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium">Reporte mensual</span>
                  </div>
                  <span className="text-xs text-green-600">15 Jun</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  )
} 